/**
 * @file: The state of one Compose session.
 *
 * Nothing reaches the server before Save, so the counts, the pins, the seed and
 * the order are here and not in the database. The class holds local copies of
 * the playlist rows. A count is a number of rows, so a change of count adds or
 * removes rows, and nothing new is stored.
 *
 * The class has no Angular dependency. The component gives it the playlist
 * items, the catalogue of media the operator can add, the scene, the other
 * scenes and the labels. It then reads back the recipe lines, the sequence and
 * the sentences that the template shows.
 *
 * The order rules are in `shared/playlist-spread`, because the server writes
 * the order this class shows. With one implementation the preview and the
 * stored order stay the same.
 */

import {
  DEFAULT_RULES, rowKey, spread, violations, closestRepeat, repeatWaits, kindWaits, outcomes,
  kindShares, loopMs, validPins
} from '../../../../shared/playlist-spread'
import { MAX_POSTS, playsPosts, postsOf, durationForPosts } from '../../../../shared/media-types'
import { formatDuration } from 'common/clock'
import { History } from './history'
import { kindCodes } from './kind-codes'
import { placeBumpers, takeoverName } from './takeover'
import {
  Catalogue, describeMedia, unknownMedia, colourOf, rowBlock, blockedBy, mediaRef,
  mediaKey, compareNames, nameMatcher, BLOCKER_SENTENCE, NO_KIND_NAME, UNKNOWN_NAME
} from './catalogue'

/**
 * The seed of the first spread of a session. Until then the seed is null: the
 * stored order came from the database, and no seed made it. The header showed
 * "Seed 1" over that order once, and everyone read it as the result of seed 1.
 */
const FIRST_SEED = 1

/** The seed a stored order has. */
const NO_SEED = null

/** The seed a derived list starts from. Regenerate gives another order. */
const TAKEOVER_SEED = 1

/** The posts of a length, for a media type that plays posts. Else nothing. */
const postsFor = (mediaType, durationMs) =>
  (playsPosts(mediaType) ? postsOf(durationMs) : null)

/** Rows before candidates; rows by name; candidates keep their newest-first order. */
const byLinePlace = (a, b) => {
  if ((a.count > 0) !== (b.count > 0)) return a.count > 0 ? -1 : 1
  return a.count > 0 ? compareNames(a.media.name || '', b.media.name || '') : 0
}

/**
 * The rows of a playlist in the shape the transformer stores.
 *
 * The shared spread reads `durationMs`, and the column is `duration`, so the
 * name of the field changes here and the number does not. The takeover panel
 * writes a list of its own, and it uses this function, so one shape reaches the
 * server from both writes.
 *
 * @param {Array} rows - The rows of a playlist, in the order they play
 * @returns {Array} `{ mediaType, mediaId, duration }` for each row
 */
const itemsOf = rows => rows.map(row => ({
  mediaType: row.mediaType,
  mediaId: row.mediaId,
  duration: row.durationMs
}))

/** A length of time, written the way the rest of the page writes one. */
const clock = formatDuration

/**
 * A rule break, as a sentence. The radio schedulers name the rows and the slot,
 * because "the list is not random enough" is not something an operator can act
 * on. The slots of the shared module count from zero, and the list counts from
 * one. A count break has no slot, because the count is wrong at every slot.
 */
const SENTENCE = Object.freeze({
  count: (names, { count, minGap }, { length }) => `${names[0]} ×${count} cannot stay ${minGap} apart in ${length} slots`,
  countTime: (names, { count, minGapMs }, { loopMs: loop }) => `${names[0]} ×${count} cannot stay ${clock(minGapMs)} apart in a ${clock(loop)} loop`,
  repeat: (names, { slots, gap }) => `${names[1]} returns after ${gap} slots at slot ${slots[1] + 1}`,
  wait: (names, { slots, waitMs }) => `${names[1]} comes back after ${clock(waitMs)} at slot ${slots[1] + 1}`,
  kind: (names, { slots }) => `${names[0]} and ${names[1]} are neighbours at slot ${slots[0] + 1}`,
  long: (names, { slots }) => `${names[0]} and ${names[1]} are both long at slot ${slots[0] + 1}`
})

class ComposeState {
  constructor () {
    this.rows = []
    this.pins = {}
    this.seed = NO_SEED
    this.labelFilter = new Set()
    this.allMedia = false
    this.dirty = false

    // The media the operator can add, described once. The session asks it and
    // never holds a copy of a media of its own.
    this.catalogue = new Catalogue()
    // The steps Undo goes back over. Nothing is written before Save, so a step
    // back costs nothing.
    this._history = new History()
    this._nextLocalId = 1
  }

  /**
   * Open a playlist. The stored order stands: to open Compose changes nothing,
   * so the operator can look at a list without a risk of a new order.
   *
   * `items` are the rows as the server sends them, in the stored order.
   * `catalogue` is every media the operator can add, as
   * `{ mediaType, mediaId, name, labels, durationMs, thumbnailUrl,
   * orientations, createdAt }`. `screens` are the scenes the playlist plays on,
   * from `screensOf`, and a media needs an asset for every one of them.
   */
  load (items, catalogue, screens, scenes, labels) {
    // A full start. The catalogue keeps a label list and a set of scene names
    // when it is given an empty one, and those belong to the playlist before.
    this.catalogue.reset()
    this.catalogue.setScreens(screens)
    this.catalogue.setSources(catalogue, labels || [], scenes)

    const stored = items || []
    const known = this.catalogue.byRef()
    this.rows = stored.map(item => this._rowOf(item, known))
    this.pins = {}
    this.seed = NO_SEED
    this.labelFilter = new Set()
    this.allMedia = false
    // A step back into another playlist would make no sense.
    this._history.clear()

    // The duration column accepts null, and an old row can have none. Such a
    // row now has the length the catalogue gives it. That is a change the
    // playlist does not hold yet, so the session starts with unsaved work.
    this.dirty = this.rows.some((row, slot) => !stored[slot].duration && row.durationMs > 0)
  }

  /**
   * Replace what the label chips offer, and change nothing the operator did.
   *
   * The lists of the app arrive after login, so a reload straight onto Compose
   * can build the catalogue before the media are there. The rows, the pins, the
   * seed and the label filter of the session must survive that. Only the
   * candidates are new, and the media data of a row the catalogue could not
   * describe at load.
   *
   * `labels` and `scenes` are optional. An empty one is a list that has not
   * arrived, and the session then keeps the one it has.
   */
  setCatalogue (catalogue, labels, scenes) {
    this.catalogue.setSources(catalogue, labels, scenes)

    const known = this.catalogue.byRef()

    // A row the catalogue did not hold has only the name and the labels the
    // playlist sent with it. The catalogue now gives it the thumbnail, the kind
    // and the orientation. A row the catalogue already held keeps its slot, its
    // id and its length, so no pin and no order changes.
    this.rows = this.rows.map(row => {
      const media = known.get(mediaRef(row))
      return media ? this._reface(row, media) : row
    })
  }

  // ===== Queries =====

  itemCount () {
    return this.rows.length
  }

  /**
   * How many media the playlist holds. One media at two lengths is one media,
   * because the operator counts the films and not the lines.
   */
  mediaCount () {
    return new Set(this.rows.map(mediaRef)).size
  }

  totalMs () {
    return loopMs(this.rows)
  }

  isPinned (slot) {
    return Object.prototype.hasOwnProperty.call(this.pins, slot)
  }

  /**
   * This query gives one line for every media the recipe can hold: one line per
   * key of the playlist, then the candidates the labels add, at count 0.
   *
   * A row is never hidden by the label filter. The playlist is what the screen
   * plays, so it is always shown in full. A media that is in the playlist is
   * never offered as a candidate, whatever length the row has: the line of that
   * row is where its count is changed.
   *
   * `view` holds the two controls of the list itself: `search` on the name, and
   * `hideUnused` for the candidates at count 0.
   */
  lines (view = {}) {
    const firstRow = this.rows.reduce(
      (rows, row) => (rows.has(row.key) ? rows : rows.set(row.key, row)),
      new Map()
    )
    const rowLines = outcomes(this.rows).map(outcome => {
      const row = firstRow.get(outcome.key)

      return this._lineOf(row.media, {
        key: outcome.key,
        count: outcome.count,
        outcome,
        durationMs: row.durationMs,
        available: row.available,
        blockerNote: row.blockerNote
      })
    })

    const inPlaylist = new Set(this.rows.map(mediaRef))
    const candidateLines = this._candidates()
      .filter(media => !inPlaylist.has(mediaRef(media)))
      .map(media => this._lineOf(media, { key: mediaKey(media), count: 0 }))

    const matches = nameMatcher(view.search)

    return [...rowLines, ...candidateLines]
      .filter(line => matches(line.media))
      .filter(line => !view.hideUnused || line.count > 0)
  }

  /**
   * This query gives the recipe as the template draws it: one group per kind,
   * with the share of time of that kind and the lines under it. The shares are a
   * parameter, so a caller that shows them beside the groups measures them only
   * once.
   */
  groups (view = {}, shares = this.shares()) {
    const byKind = new Map(shares.map(share => [share.kind, share]))

    return this._byKind(this.lines(view)).map(([kind, lines]) => {
      const share = byKind.get(kind) || { count: 0, ms: 0, fraction: 0 }

      return {
        kind,
        name: kind || NO_KIND_NAME,
        colour: colourOf(kind, this.catalogue.colours),
        count: share.count,
        ms: share.ms,
        fraction: share.fraction,
        lines
      }
    })
  }

  /** This query says how much of the loop each kind takes. The bars show it. */
  shares () {
    return kindShares(this.rows).map(share => ({
      ...share,
      name: share.kind || NO_KIND_NAME,
      colour: colourOf(share.kind, this.catalogue.colours)
    }))
  }

  /**
   * The media of one recipe line. A line is a row of the playlist or a
   * candidate, and the two are found in different places.
   */
  mediaOfLine (key) {
    const row = this.rows.find(one => one.key === key)

    return row ? row.media : (this.catalogue.mediaByLineKey(key) || null)
  }

  /**
   * The media of one row of the sequence. The id comes from the template, so it
   * arrives as a string.
   */
  mediaOfRow (id) {
    const row = this.rows.find(one => String(one.id) === String(id))

    return row ? row.media : null
  }

  /**
   * The names of the scenes the labels of the app can name. A caller that has
   * to tell a kind label from a scene label needs them.
   */
  get sceneNames () {
    return this.catalogue.context.sceneNames
  }

  /** This query gives the label chips. A chip that is on adds its media. */
  /**
   * The label chips. The count is what a click on the chip adds, so the media
   * the list already holds are taken out. This class knows the rows, and the
   * catalogue knows the media of each label.
   */
  labels () {
    return this.catalogue.labelChips(this.labelFilter, this._rowRefs())
  }

  /** The media the list holds, as references, for a query about candidates. */
  _rowRefs () {
    return new Set(this.rows.map(row => mediaRef(row)))
  }

  sentences () {
    const context = { length: this.rows.length, loopMs: loopMs(this.rows) }
    return violations(this.rows).map(violation => SENTENCE[violation.rule](this._namesFor(violation), violation, context))
  }

  /**
   * The rows the server will not accept, as sentences. The whole write fails on
   * one such row, so Save has to wait until the operator removes it.
   */
  blockers () {
    return this.rows
      .filter(row => !row.available)
      .map(row => BLOCKER_SENTENCE[row.block.blocker](row.media.name, row.block.screen))
  }

  /** This query says whether a step back is possible. */
  canUndo () {
    return this._history.canUndo()
  }

  /** This query gives the media a takeover can use as its bumper. */
  bumperChoices (search, screens) {
    return this.catalogue.bumperChoices(search, screens)
  }

  /** This query gives the distinct media of the rows, for the takeover panel. */
  rowMedia () {
    return this.catalogue.rowMedia(this.rows)
  }

  /**
   * This query gives the rows of a takeover of this list, and the pins that
   * hold them.
   *
   * The copy is what the operator sees: the pinned rows keep their slots, and
   * the rest is spread again around the bumper. `replaceRef` names a media to
   * remove first, which is how one day kept its length when a bumper came in.
   */
  takeoverRows (bumper, {
    count = 1, first = true, replaceRef = null, screens = this.catalogue.screens
  } = {}) {
    const kept = this.rows.filter(row => !replaceRef || mediaRef(row) !== replaceRef)
    const pinnedIds = new Set(Object.values(this.pins))
    // A pin names a slot, and a removed row moves every later row up one.
    const pins = Object.fromEntries(kept
      .map((row, slot) => [slot, row.id])
      .filter(([, id]) => pinnedIds.has(id)))
    // The bumper is described the way every other media is, so the blocker rule
    // reaches it as well. A caller may hand over a plain catalogue entry.
    const media = this.catalogue.mediaByRef(mediaRef(bumper)) ||
      describeMedia(bumper, this.catalogue.context)
    const bumperRows = Array.from({ length: Math.max(1, Math.floor(count) || 1) },
      () => this._rowFor(media.mediaType, media.mediaId, media.durationMs, media, screens))

    // The rows of the copy are checked against the scene the copy goes to, so a
    // row that could not play on the source list can still play on the copy.
    const copied = kept.map(row => ({ ...row, ...this._face(row.durationMs, row.media, screens) }))

    return placeBumpers(copied, bumperRows, {
      first,
      pins,
      seed: this.seed ?? TAKEOVER_SEED
    })
  }

  /** This query gives the name a takeover of this list would have. */
  takeoverName (baseName, bumperName) {
    return takeoverName(baseName, bumperName)
  }

  /** This query says whether Save can run at all. */
  canSave () {
    return this.rows.length > 0 && this.rows.every(row => row.available)
  }

  /**
   * Everything behind the order, as data, for the debug view. Slots count from
   * one, as the list shows them. Gaps are given in slots and in seconds, so a
   * distribution that looks wrong can be read off without the screen.
   */
  debugDump () {
    const rows = this.rows
    const n = rows.length
    const total = loopMs(rows)
    const gapSlots = (slots, i) => (i + 1 < slots.length ? slots[i + 1] - slots[i] : slots[0] + n - slots[i])
    const repeats = repeatWaits(rows).map(({ key, slots, waits }) => ({
      key,
      name: rows[slots[0]].media.name,
      count: slots.length,
      slots: slots.map(slot => slot + 1),
      gapsInSlots: slots.map((_, i) => gapSlots(slots, i)),
      gapsInSeconds: waits.map(wait => Math.round(wait / 1000))
    }))

    const codes = kindCodes(rows.map(row => row.kind))
    const kinds = kindWaits(rows).map(({ key, slots }) => ({
      kind: key,
      code: codes.get(key),
      count: slots.length,
      slots: slots.map(slot => slot + 1),
      gapsInSlots: slots.map((_, i) => gapSlots(slots, i))
    }))

    return {
      seed: this.seed,
      rules: DEFAULT_RULES,
      pins: this.pins,
      loopSeconds: Math.round(total / 1000),
      kindCodes: Object.fromEntries([...codes].filter(([kind]) => kind != null).map(([kind, code]) => [code, kind])),
      kindsBySlot: rows.map(row => codes.get(row.kind)).join(' '),
      kinds,
      sequence: rows.map((row, slot) => ({
        slot: slot + 1,
        id: row.id,
        key: row.key,
        name: row.media.name,
        kind: row.kind,
        seconds: row.durationMs / 1000,
        pinned: this.isPinned(slot)
      })),
      repeats,
      violations: violations(rows),
      shares: this.shares()
    }
  }

  /**
   * The repeated media whose waits differ a lot: the longest wait is at least
   * twice the shortest. Evenness is not a rule, so it is not a break. A stored
   * order shows it here, and Regenerate is the answer.
   */
  unevenWaits () {
    return repeatWaits(this.rows)
      .map(({ key, slots, waits }) => ({ name: this.rows[slots[0]].media.name, shortestMs: Math.min(...waits), longestMs: Math.max(...waits) }))
      .filter(({ shortestMs, longestMs }) => longestMs >= 2 * shortestMs)
  }

  closestRepeat () {
    return closestRepeat(this.rows)
  }

  /**
   * This query gives the rules the order is made under, as text for the footer.
   * The footer lists them after "Rules:", so each one starts in lower case.
   */
  rules () {
    return [
      `same media at least ${DEFAULT_RULES.minGap} slots and ${clock(DEFAULT_RULES.minGapMs)} apart`,
      DEFAULT_RULES.sameKindApart ? 'never two of a kind in a row' : null,
      DEFAULT_RULES.shortAfterLong ? 'a short one after a long one' : null
    ].filter(Boolean)
  }

  /**
   * What Save sends. One write replaces every row and every order (D7). The
   * shared spread reads `durationMs`, and the transformer stores `duration`, so
   * the name of the field changes here and the number does not.
   */
  payload (sceneCollectionId, rows = this.rows) {
    return { sceneCollectionId, items: itemsOf(rows) }
  }

  // ===== Commands =====

  /**
   * Set how many times a media is in the loop. Rows are added or removed, and
   * the spread then solves the order again. The seed is kept and the pinned rows
   * hold their slots, so only the rows without a pin move (D5).
   */
  setCount (key, count) {
    if (!Number.isFinite(count)) {
      return
    }

    const wanted = Math.max(0, Math.floor(count))
    const present = this.rows.filter(row => row.key === key).length

    if (wanted === present) {
      return
    }

    const rows = wanted > present
      ? this._withMore(key, wanted - present)
      : this._withFewer(key, present - wanted)

    if (rows === this.rows) {
      return
    }

    this._remember()
    this._resolve(rows)
    this.dirty = true
  }

  /**
   * Set how many posts a social item plays each time it is on screen.
   *
   * The duration of the row is the number of posts, so the key of the row
   * changes with it. The order stands: only the lengths change, and a new order
   * would surprise the operator who asked for one more post.
   */
  setPosts (key, posts) {
    if (!Number.isFinite(posts)) {
      return
    }

    const durationMs = durationForPosts(posts)
    const [first] = this.rows.filter(row => row.key === key)

    if (!first || !playsPosts(first.mediaType) || first.durationMs === durationMs) {
      return
    }

    this._remember()
    this.rows = this.rows.map(row => (row.key === key ? this._relength(row, durationMs) : row))
    this.dirty = true
  }

  /** This command gives a new order for the same rows. The pins hold slots. */
  regenerate () {
    if (this.rows.length < 2) {
      return
    }

    this._remember()
    this.seed = this.seed === NO_SEED ? FIRST_SEED : this.seed + 1
    this.rows = spread(this.rows, { seed: this.seed, pins: this.pins })
    this.dirty = true
  }

  /**
   * Move one row and shift its neighbours, as the playlist editor does today.
   * The moved row is then pinned, so the next spread leaves it where the
   * operator put it (D5).
   */
  moveRow (fromSlot, toSlot) {
    if (fromSlot === toSlot || !this.rows[fromSlot] || !this.rows[toSlot]) {
      return
    }

    this._remember()

    const pinnedIds = new Set(Object.values(this.pins))
    const rows = [...this.rows]
    const [moved] = rows.splice(fromSlot, 1)
    rows.splice(toSlot, 0, moved)

    // A pin names a slot, and the move gave most rows a new slot. The row ids
    // do not change, so a pin is at the slot where its row now is.
    this.pins = Object.fromEntries(rows
      .map((row, slot) => [slot, row.id])
      .filter(([, id]) => pinnedIds.has(id) || id === moved.id))
    this.rows = rows
    this.dirty = true
  }

  togglePin (slot) {
    const row = this.rows[slot]

    if (!row) {
      return
    }

    this._remember()

    const pins = { ...this.pins }

    if (pins[slot] === row.id) {
      delete pins[slot]
    } else {
      pins[slot] = row.id
    }

    this.pins = pins
    this.dirty = true
  }

  /**
   * Go back one step. Every command that changes the rows, the pins or the seed
   * leaves a step behind, so anything the operator did since Compose opened can
   * be taken back.
   *
   * The session stays marked as unsaved. A step back is still a change against
   * the stored playlist until the operator saves or leaves.
   */
  undo () {
    const step = this._history.last()

    if (!step) {
      return
    }

    this._history.drop()

    // The rows are built again, so a media the catalogue has since described
    // shows its name, its kind and its blocker as they are now.
    const known = this.catalogue.byRef()
    const shown = new Map(this.rows.map(row => [mediaRef(row), row.media]))
    this.rows = step.items.map(item => {
      const media = known.get(mediaRef(item)) || shown.get(mediaRef(item)) ||
        unknownMedia({ ...item, duration: item.durationMs }, this.catalogue.context)

      return this._rowFor(item.mediaType, item.mediaId, item.durationMs, media)
    })
    // A step holds the pinned slots. The rows are new objects with new ids, and
    // the slots are the same, so each pin points at the row of its slot.
    this.pins = this._pinsAtSlots(step.pins, this.rows)
    this.seed = step.seed
    this.dirty = true
  }

  /** The playlist now holds what the session holds, so nothing is unsaved. */
  markSaved () {
    this.dirty = false
  }

  unpinAll () {
    if (Object.keys(this.pins).length === 0) {
      return
    }

    this._remember()
    this.pins = {}
    this.dirty = true
  }

  /** Offer every media of the library as a candidate, whatever the chips say. */
  toggleAllMedia () {
    this.allMedia = !this.allMedia
  }

  toggleLabel (labelId) {
    const filter = new Set(this.labelFilter)

    if (filter.has(labelId)) {
      filter.delete(labelId)
    } else {
      filter.add(labelId)
    }

    this.labelFilter = filter
  }

  // ===== Internals =====

  _localId () {
    return `local-${this._nextLocalId++}`
  }

  /**
   * Keep the state as it is before a command changes it.
   *
   * A step holds only the data the commands own: which media is in which slot,
   * at which length, and where the pins are. The name, the kind and the blocker
   * of a media come from the catalogue, so a step back reads the catalogue of
   * that moment and never an old copy of it.
   */
  _remember () {
    this._history.remember({
      items: this.rows.map(({ mediaType, mediaId, durationMs }) => ({ mediaType, mediaId, durationMs })),
      pins: this.pins,
      seed: this.seed
    })
  }

  /** This query gives the pins of a set of slots, pointing at the rows there. */
  _pinsAtSlots (pins, rows) {
    return Object.fromEntries(Object.keys(pins)
      .map(Number)
      .filter(slot => rows[slot])
      .map(slot => [slot, rows[slot].id]))
  }

  /** This query returns the same row at another length, so with another key. */
  _relength (row, durationMs) {
    return {
      ...row,
      key: rowKey({ mediaType: row.mediaType, mediaId: row.mediaId, duration: durationMs }),
      ...this._face(durationMs, row.media)
    }
  }

  /**
   * One recipe line. A row of the playlist and a candidate of a label chip
   * differ in the count and the outcome, and in nothing else the line shows.
   */
  _lineOf (media, { key, count, outcome = null, durationMs, available, blockerNote }) {
    // A row can be longer than the media the catalogue holds: a social row is
    // as long as the posts the operator asked for.
    const length = durationMs === undefined ? media.durationMs : durationMs

    return {
      key,
      kind: media.kind,
      media,
      count,
      durationMs: length,
      posts: postsFor(media.mediaType, length),
      maxPosts: playsPosts(media.mediaType) ? MAX_POSTS : null,
      outcome,
      available: available === undefined ? media.available : available,
      blockerNote: blockerNote === undefined ? media.blockerNote : blockerNote
    }
  }

  /**
   * This query gives the media names a sentence needs. A sentence with slots
   * needs one name for each slot. A count sentence needs the name of its key.
   */
  _namesFor (violation) {
    if (violation.slots) {
      return violation.slots.map(slot => this.rows[slot].media.name)
    }

    const row = this.rows.find(one => one.key === violation.key)

    return [row ? row.media.name : UNKNOWN_NAME]
  }

  /**
   * A row of the playlist. The duration column accepts null, so a stored row
   * can arrive without one. The catalogue then gives the length, by the same
   * rule the server uses when it writes a row.
   */
  _rowOf (item, known) {
    const media = known.get(mediaRef(item)) || unknownMedia(item, this.catalogue.context)
    return this._rowFor(item.mediaType, item.mediaId, item.duration || media.durationMs, media)
  }

  /**
   * A row with no length is zero, so every sum holds, and it is a row the
   * operator has to remove before Save.
   */
  _rowFor (mediaType, mediaId, durationMs, media, screens = this.catalogue.screens) {
    const length = durationMs || 0

    return {
      id: this._localId(),
      mediaType,
      mediaId,
      key: rowKey({ mediaType, mediaId, duration: length }),
      ...this._face(length, media, screens)
    }
  }

  /** This query returns the same row, with the media the catalogue describes. */
  _reface (row, media) {
    return { ...row, ...this._face(row.durationMs, media) }
  }

  /** This query gives the parts of a row that come from its media and length. */
  _face (durationMs, media, screens = this.catalogue.screens) {
    return {
      kind: media.kind,
      durationMs,
      media,
      ...blockedBy(rowBlock(durationMs, media, screens))
    }
  }

  /**
   * This query gives the media offered as candidates, newest upload first: the
   * whole library when All media is on, else the media of the label chips.
   */
  _candidates () {
    return this.catalogue.candidates({ allMedia: this.allMedia, labelFilter: this.labelFilter })
  }

  _withMore (key, howMany) {
    const existing = this.rows.find(row => row.key === key)
    const media = this.mediaOfLine(key)

    // A media the server would reject must not enter the list at all.
    if (!media || (!existing && !media.available)) {
      return this.rows
    }

    const durationMs = existing ? existing.durationMs : media.durationMs
    const fresh = Array.from({ length: howMany },
      () => this._rowFor(media.mediaType, media.mediaId, durationMs, media))

    return [...this.rows, ...fresh]
  }

  /**
   * Remove rows from the end of the loop, and take the rows without a pin
   * first. A pin is the answer to "keep this one here", so it goes last.
   */
  _withFewer (key, howMany) {
    const pinned = new Set(Object.values(this.pins))
    const fromTheEnd = [...this.rows].reverse().filter(row => row.key === key)
    const doomed = new Set([
      ...fromTheEnd.filter(row => !pinned.has(row.id)),
      ...fromTheEnd.filter(row => pinned.has(row.id))
    ].slice(0, howMany).map(row => row.id))

    return this.rows.filter(row => !doomed.has(row.id))
  }

  /**
   * A new set of rows, spread with the seed of the session. The first spread of
   * a session takes the first seed. The pins are put through the rule of the
   * shared module, which is the rule the spread itself honours them by.
   */
  _resolve (rows) {
    this.seed = this.seed ?? FIRST_SEED
    this.pins = Object.fromEntries(validPins(this.pins, rows))
    this.rows = spread(rows, { seed: this.seed, pins: this.pins })
  }

  /**
   * Group the lines by kind, in a fixed order, so a line keeps its place when a
   * count changes and the sequence is generated again. Kinds with rows come
   * first, the kind with the most time first. Kinds with candidates only come
   * after, by name. Inside a kind the rows come first, by name, then the
   * candidates, newest first.
   */
  _byKind (lines) {
    const msByKind = new Map()
    this.rows.forEach(row => msByKind.set(row.kind, (msByKind.get(row.kind) || 0) + row.durationMs))
    const nameOf = kind => kind || NO_KIND_NAME
    const byKind = new Map()
    lines.forEach(line => {
      const group = byKind.get(line.kind)

      if (group) {
        group.push(line)
      } else {
        byKind.set(line.kind, [line])
      }
    })

    return [...byKind.keys()]
      .sort((a, b) => (msByKind.get(b) || 0) - (msByKind.get(a) || 0) || compareNames(nameOf(a), nameOf(b)))
      .map(kind => [kind, byKind.get(kind).sort(byLinePlace)])
  }
}

export { ComposeState, itemsOf }
